import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../state/cart/CartContext';
import { useAuth } from '../../state/auth/AuthProvider';
import { cartLinePrice, isDealCartLine, isItemCartLine } from '../../state/cart/cartTypes';
import { ClearCartConfirmModal } from './ClearCartConfirmModal';
import { PromoCodeInput } from './PromoCodeInput';
import { CartItemNotesField, ItemLineExtras } from './CartItemNotesField';
import { useOrderingAvailability } from '../../hooks/useOrderingAvailability';

function formatGBP(n: number) {
  return `£ ${n.toFixed(2)}`;
}

export function CartDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const cart = useCart();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { acceptingOrders, message: orderingMessage, reason } = useOrderingAvailability();
  const [confirmClearOpen, setConfirmClearOpen] = React.useState(false);
  const isStaff = role === 'admin' || role === 'employee';
  const checkoutBlocked = !acceptingOrders && !isStaff;

  if (!isOpen) return null;

  const subtotal = cart.subtotal;
  const discount = cart.discount;
  const total = cart.total;

  return createPortal(
    <>
      <div
        className="chunkzters-drawerOverlay"
        role="button"
        tabIndex={0}
        aria-label="Close cart"
        onClick={onClose}
        onKeyDown={(e) => (e.key === 'Enter' ? onClose() : undefined)}
      />
      <div className="chunkzters-drawer" role="dialog" aria-modal="true" aria-label="Your cart">
        <div className="chunkzters-drawerHeader">
          <div className="fw-bold text-white flex-grow-1 min-w-0">Your Cart</div>
          <button
            type="button"
            className="btn btn-outline-light chunkzters-drawerClose"
            aria-label="Close cart"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <i className="bi bi-x-lg" aria-hidden />
          </button>
        </div>

        <div className="chunkzters-drawerBody">
          {cart.state.lines.length === 0 ? (
            <div className="chunkzters-card p-3">
              <div className="fw-bold mb-1">Basket is empty</div>
              <div style={{ color: 'var(--chunkzters-muted)' }} className="small">
                Add something tasty from the menu.
              </div>
              <Link
                to="/"
                className="btn btn-sm chunkzters-btn-primary mt-3 d-inline-flex align-items-center gap-2"
                onClick={onClose}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                Back to menu
              </Link>
            </div>
          ) : (
            <>
              <div className="d-grid gap-3">
                {cart.state.lines.map((l) => (
                  <div key={l.lineId} className="chunkzters-card p-3">
                    <div className="d-flex gap-3">
                      <img
                        src={
                          isItemCartLine(l)
                            ? l.item.imageUrl || `${process.env.PUBLIC_URL}/assets/homepage_2.png`
                            : l.dealImageUrl || `${process.env.PUBLIC_URL}/assets/homepage_2.png`
                        }
                        alt={isDealCartLine(l) ? l.dealName : l.item.name}
                        style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div>
                            <div className="fw-semibold">{isDealCartLine(l) ? l.dealName : l.item.name}</div>
                            {isDealCartLine(l) ? (
                              <ul className="small mb-0 ps-3 mt-1" style={{ color: 'var(--chunkzters-muted)' }}>
                                {l.selections.map((s) => (
                                  <li key={`${s.slotId}-${s.menuItemId}-${s.slotLabel}`}>{s.slotLabel}: {s.menuItemName}</li>
                                ))}
                              </ul>
                            ) : isItemCartLine(l) ? (
                              <ItemLineExtras
                                drinkName={l.drinkSelection?.menuItemName}
                                notes={l.notes}
                                isMeal={l.isMeal}
                                flavourChoiceLabel={l.flavourChoice?.label}
                                saladSauceLabel={l.saladSaucePreference?.label}
                              />
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => cart.removeLine(l.lineId)}
                            aria-label="Remove item"
                          >
                            <i className="bi bi-trash3" />
                          </button>
                        </div>

                        {isItemCartLine(l) ? (
                          <div className="mt-2">
                            <CartItemNotesField
                              value={l.notes ?? ''}
                              onChange={(value) => cart.setLineNotes(l.lineId, value)}
                            />
                          </div>
                        ) : null}

                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => cart.setQuantity(l.lineId, l.quantity - 1)}
                              aria-label="Decrease quantity"
                            >
                              <i className="bi bi-dash-lg" />
                            </button>
                            <div
                              className="px-2 py-1 rounded"
                              style={{
                                background: 'var(--chunkzters-brand)',
                                border: '1px solid var(--chunkzters-brand)',
                                minWidth: 30,
                                textAlign: 'center',
                                fontWeight: 800,
                                color: 'var(--chunkzters-white)',
                              }}
                            >
                              {l.quantity}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => cart.setQuantity(l.lineId, l.quantity + 1)}
                              aria-label="Increase quantity"
                            >
                              <i className="bi bi-plus-lg" />
                            </button>
                          </div>
                          <div className="fw-bold">{formatGBP(l.quantity * cartLinePrice(l))}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="my-3 chunkzters-divider" />

              <div className="d-flex align-items-center justify-content-between">
                <div className="fw-semibold">Subtotal:</div>
                <div className="fw-bold">{formatGBP(subtotal)}</div>
              </div>

              <div className="mt-3 small" style={{ color: 'var(--chunkzters-muted)' }}>
                Any offers cannot be used in conjunction with other offer.
              </div>

              <div className="mt-3">
                <PromoCodeInput />
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-sm chunkzters-btn-primary d-inline-flex align-items-center gap-2"
                  onClick={() => setConfirmClearOpen(true)}
                >
                  <i className="bi bi-trash" aria-hidden />
                  Clear cart
                </button>
              </div>
            </>
          )}
        </div>

        <div className="chunkzters-drawerFooter">
          <div className="d-flex align-items-center justify-content-between">
            <div className="fw-semibold">Subtotal:</div>
            <div className="fw-bold">{formatGBP(subtotal)}</div>
          </div>
          <div className="d-flex align-items-center justify-content-between mt-1">
            <div className="fw-semibold">Discount:</div>
            <div className="fw-bold">{discount > 0 ? `- ${formatGBP(discount)}` : formatGBP(0)}</div>
          </div>
          <div className="d-flex align-items-center justify-content-between mb-3 mt-1">
            <div className="fw-semibold">Total:</div>
            <div className="fw-bold">{formatGBP(total)}</div>
          </div>
          {checkoutBlocked && orderingMessage ? (
            <div className="alert alert-warning small mb-3" role="status">
              {orderingMessage}
            </div>
          ) : null}
          <button
            type="button"
            className="btn chunkzters-btn-primary w-100"
            disabled={cart.state.lines.length === 0 || checkoutBlocked}
            onClick={() => {
              onClose();
              navigate('/checkout');
            }}
          >
            {checkoutBlocked
              ? reason === 'outside_hours'
                ? 'Closed'
                : 'Orders paused'
              : 'Go to Checkout'}
          </button>
        </div>
      </div>

      {confirmClearOpen ? (
        <ClearCartConfirmModal
          onCancel={() => setConfirmClearOpen(false)}
          onConfirm={() => {
            cart.clear();
            setConfirmClearOpen(false);
          }}
        />
      ) : null}
    </>,
    document.body
  );
}

